import { useEffect } from "react"
import { ogImagePath, SITE_NAME, type PageMeta } from "@shared/pages"

export type DocumentMetaOptions = {
  /** Canonical path for the current route (e.g. "/about") */
  path: string
  /** Cache-busting version for the social image (event updatedAt) */
  version?: number
}

function upsertMeta(attribute: "name" | "property", key: string, content: string): void {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  if (existing) {
    existing.setAttribute("content", content)
    return
  }
  const tag = document.createElement("meta")
  tag.setAttribute(attribute, key)
  tag.setAttribute("content", content)
  document.head.appendChild(tag)
}

function upsertCanonical(href: string): void {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (existing) {
    existing.setAttribute("href", href)
    return
  }
  const link = document.createElement("link")
  link.setAttribute("rel", "canonical")
  link.setAttribute("href", href)
  document.head.appendChild(link)
}

/**
 * Mirrors the server-injected head tags during client-side navigation. Updates
 * the existing tags in place (no duplicates) and creates them only when a
 * route was reached outside the server-rendered shell.
 */
export function applyDocumentMeta(meta: PageMeta, { path, version }: DocumentMetaOptions): void {
  const origin = window.location.origin
  const url = `${origin}${path}`
  const image = `${origin}${ogImagePath(meta.og, version)}`

  document.title = meta.title
  upsertMeta("name", "description", meta.description)
  upsertCanonical(url)
  upsertMeta("property", "og:type", "website")
  upsertMeta("property", "og:site_name", SITE_NAME)
  upsertMeta("property", "og:title", meta.title)
  upsertMeta("property", "og:description", meta.description)
  upsertMeta("property", "og:url", url)
  upsertMeta("property", "og:image", image)
  upsertMeta("property", "og:image:width", "1200")
  upsertMeta("property", "og:image:height", "630")
  upsertMeta("name", "twitter:card", "summary_large_image")
  upsertMeta("name", "twitter:title", meta.title)
  upsertMeta("name", "twitter:description", meta.description)
  upsertMeta("name", "twitter:image", image)
}

export function useDocumentMeta(meta: PageMeta | null, options: DocumentMetaOptions): void {
  const { path, version } = options

  useEffect(() => {
    if (!meta) return
    applyDocumentMeta(meta, { path, version })
  }, [meta, path, version])
}
